"use client";

import React from "react";

type Phase = { label: string; statuses: number[] };

// Quy trình THƯỜNG.
const NORMAL_PHASES: Phase[] = [
  { label: "Tiếp nhận", statuses: [1] },                 // đợi xác nhận
  { label: "Lấy hàng", statuses: [2] },                  // tài xế lấy + mang về
  { label: "Kiểm tra", statuses: [10] },                 // kiểm tra / phân loại
  { label: "Giặt sấy", statuses: [3, 4, 11, 12, 13, 14, 15, 16, 17] },
  { label: "Sẵn sàng giao", statuses: [6] },
  { label: "Giao hàng", statuses: [7] },                 // đang giao
  { label: "Hoàn thành", statuses: [5] },
];

// Quy trình CÓ KIỂM ĐẾM (requires_counting=1): thêm pha kiểm đếm nhận (trước lấy hàng)
// và kiểm đếm giao (trước hoàn thành).
const COUNTING_PHASES: Phase[] = [
  { label: "Tiếp nhận", statuses: [1] },
  { label: "Kiểm đếm nhận", statuses: [9, 19, 20] },     // kiểm đếm với khách / xong / chờ giao TX
  { label: "Lấy hàng", statuses: [2] },
  { label: "Kiểm tra", statuses: [10] },
  { label: "Giặt sấy", statuses: [3, 4, 11, 12, 13, 14, 15, 16, 17] },
  { label: "Sẵn sàng giao", statuses: [6] },
  { label: "Giao hàng", statuses: [7] },
  { label: "Kiểm đếm giao", statuses: [18] },            // kiểm đếm với khách khi giao
  { label: "Hoàn thành", statuses: [5] },
];

const TRANSFERRING = 21;
const SPLIT_LABEL = "Tách đơn";
const VEHICLE_LABEL = "Xe trung chuyển";

/** Đơn đã đi xe trung chuyển ở những chặng nào (mốc suy từ log bàn giao xe). */
export type VehicleLegs = {
  /** Sau "Lấy hàng" — đơn của nhà máy, tài xế đưa hàng lên xe tại điểm tập kết */
  afterPickup?: boolean;
  /** Sau "Điều chuyển" — đơn có phiếu điều chuyển, hàng lên xe khi đang chuyển đi */
  afterTransfer?: boolean;
  /** Sau "Sẵn sàng giao" — hàng sạch rời nhà máy ra điểm tập kết */
  afterReady?: boolean;
};

// Chèn pha "Tách đơn" và/hoặc "Điều chuyển":
//  - "Tách đơn": khi đơn đã chia (hasSplit — đơn cha).
//  - "Điều chuyển": khi đang/đã điều chuyển (status=21 hoặc hasTransfer).
//    + Loại B (tạo từ Kiểm tra/Chờ giặt — đồ đã lấy về): chèn ngay SAU "Kiểm tra" như cũ.
//    + Loại A (tạo từ WAITING_ACCEPT — đơn CHƯA ai lấy hàng): chèn ngay SAU "Tiếp nhận", TRƯỚC "Lấy hàng"/
//      "Kiểm tra" — nếu không, 2 bước này sẽ bị tô ✓ hoàn thành dù chưa từng xảy ra ở chi nhánh này.
// Đơn thường không chia/không chuyển thì không hiện, tránh làm rối.
const withExtraPhases = (
  base: Phase[],
  status: number,
  hasTransfer: boolean,
  hasSplit: boolean,
  isEarlyTransfer: boolean,
  vehicleLegs: VehicleLegs,
): Phase[] => {
  // Chặng XE TRUNG CHUYỂN là sự kiện đã xảy ra (statuses rỗng), chèn ngay SAU bước mà đơn đang đứng lúc
  // bàn giao — mốc lấy từ chính log bàn giao, không đoán:
  //  - `afterPickup`   : đơn của nhà máy, tài xế lấy hàng xong đưa lên xe ở điểm tập kết → sau "Lấy hàng"
  //  - `afterTransfer` : đơn điều chuyển → PHẢI sau "Điều chuyển" (có phiếu rồi hàng mới lên xe)
  //  - `afterReady`    : hàng sạch rời nhà máy → sau "Sẵn sàng giao"
  // Chèn từ mốc XA nhất về gần để index của các mốc trước không bị dịch.
  const insertAfter = (phases: Phase[], idx: number): Phase[] => {
    if (idx < 0) return phases;
    const out = [...phases];
    out.splice(idx + 1, 0, { label: VEHICLE_LABEL, statuses: [] });
    return out;
  };

  const withVehicle = (phases: Phase[]): Phase[] => {
    let out = phases;
    if (vehicleLegs.afterReady) out = insertAfter(out, out.findIndex((p) => p.statuses.includes(6)));
    if (vehicleLegs.afterTransfer) {
      const transferIdx = out.findIndex((p) => p.statuses.includes(TRANSFERRING));
      // Không tìm thấy pha "Điều chuyển" (dữ liệu cũ/lệch) → lùi về sau "Kiểm tra" cho khỏi mất chặng.
      out = insertAfter(out, transferIdx >= 0 ? transferIdx : out.findIndex((p) => p.statuses.includes(10)));
    }
    if (vehicleLegs.afterPickup) out = insertAfter(out, out.findIndex((p) => p.statuses.includes(2)));
    return out;
  };

  if (!hasSplit && Number(status) !== TRANSFERRING && !hasTransfer) return withVehicle(base);
  const inspectIdx = base.findIndex((p) => p.statuses.includes(10)); // pha "Kiểm tra"
  if (inspectIdx < 0) return withVehicle(base);

  // ĐƠN CHA (đã chia): rút gọn còn "... → Kiểm tra → Tách đơn → Hoàn thành".
  // Các bước Giặt sấy / Sẵn sàng giao / Giao hàng KHÔNG thể hiện ở cha — chúng nằm ở từng đơn con.
  if (hasSplit) {
    const doneP = base.find((p) => p.statuses.includes(5)); // "Hoàn thành"
    const phases: Phase[] = [...base.slice(0, inspectIdx + 1), { label: SPLIT_LABEL, statuses: [] }];
    if (doneP) phases.push(doneP);
    return withVehicle(phases);
  }

  const phases = [...base];
  if (isEarlyTransfer) {
    // Loại A: chèn ngay sau "Tiếp nhận" (status=1) — trước Lấy hàng/Kiểm tra.
    const acceptIdx = base.findIndex((p) => p.statuses.includes(1));
    phases.splice((acceptIdx < 0 ? 0 : acceptIdx) + 1, 0, { label: "Điều chuyển", statuses: [TRANSFERRING] });
  } else {
    // Loại B: chèn ngay sau "Kiểm tra" như cũ.
    phases.splice(inspectIdx + 1, 0, { label: "Điều chuyển", statuses: [TRANSFERRING] });
  }
  return withVehicle(phases);
};

const OrderProgressBar: React.FC<{
  status: number;
  requiresCounting?: number;
  hasTransfer?: boolean;
  hasSplit?: boolean;
  isEarlyTransfer?: boolean;
  /** Các chặng đơn đã đi xe trung chuyển */
  vehicleLegs?: VehicleLegs;
}> = ({ status, requiresCounting, hasTransfer, hasSplit, isEarlyTransfer, vehicleLegs }) => {
  const base = Number(requiresCounting) === 1 ? COUNTING_PHASES : NORMAL_PHASES;
  const PHASES = withExtraPhases(
    base,
    Number(status),
    !!hasTransfer,
    !!hasSplit,
    !!isEarlyTransfer,
    vehicleLegs || {},
  );
  // Đơn huỷ: hiển thị riêng.
  if (status === 8) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm font-semibold text-red-600 text-center">
        Đơn đã huỷ
      </div>
    );
  }

  let curIdx = PHASES.findIndex((p) => p.statuses.includes(Number(status)));
  if (curIdx < 0) curIdx = 0;
  const completed = status === 5; // hoàn thành → mọi pha xanh

  // Loại A đã nhận xong: đơn quay lại status=1 ("Tiếp nhận") ở CHI NHÁNH MỚI — cùng status với lúc đầu, nhưng
  // "Điều chuyển" đã thực sự xảy ra rồi nên không được tô lùi lại thành "chưa tới". Đẩy curIdx lên tối thiểu
  // bằng vị trí pha "Điều chuyển".
  if (isEarlyTransfer && Number(status) === 1) {
    const transferIdx = PHASES.findIndex((p) => p.statuses.includes(TRANSFERRING));
    if (transferIdx > curIdx) curIdx = transferIdx;
  }

  // ĐƠN CHA: mọi trạng thái trung gian (con đang xử lý) → đứng ở "Tách đơn"; chỉ COMPLETE mới tới "Hoàn thành".
  if (hasSplit) {
    const splitIdx = PHASES.findIndex((p) => p.label === SPLIT_LABEL);
    curIdx = completed ? PHASES.length - 1 : (splitIdx >= 0 ? splitIdx : curIdx);
  }

  return (
    <div className="flex items-start">
      {PHASES.map((p, i) => {
        const isSplit = p.label === SPLIT_LABEL; // pha tách đơn → luôn coi là đã xong (sự kiện đã xảy ra), tô tím
        // Xe trung chuyển cũng là SỰ KIỆN đã xảy ra (chỉ hiện khi đã có lần bàn giao thật) → luôn tô xong,
        // không bao giờ là bước "đang chờ".
        const isVehicle = p.label === VEHICLE_LABEL;
        const isEvent = isSplit || isVehicle;
        const done = i < curIdx || completed || isEvent;
        const active = i === curIdx && !completed && !isEvent;
        const segmentPassed = i <= curIdx || completed || isEvent; // đoạn nối bên trái node i
        const isTransfer = p.statuses.includes(TRANSFERRING); // pha điều chuyển → tô cam cho nổi bật
        return (
          <div key={i} className="flex-1 flex flex-col items-center relative min-w-0">
            {/* Đường nối từ node trước → node này */}
            {i > 0 && (
              <div
                className={`absolute top-3.5 right-1/2 w-full h-0.5 ${segmentPassed ? "bg-green-400" : "bg-slate-200"}`}
              />
            )}
            {/* Vòng tròn bước */}
            <div
              className={`relative z-10 flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                done
                  ? isSplit
                    ? "bg-indigo-500 text-white"
                    : "bg-green-500 text-white"
                  : active
                    ? isTransfer
                      ? "bg-orange-500 text-white ring-4 ring-orange-100"
                      : "bg-blue-600 text-white ring-4 ring-blue-100"
                    : "bg-slate-200 text-slate-500"
              }`}
            >
              {done ? "✓" : i + 1}
            </div>
            <span
              className={`mt-1.5 text-[11px] leading-tight text-center px-1 ${
                active
                  ? isTransfer
                    ? "text-orange-700 font-semibold"
                    : "text-blue-700 font-semibold"
                  : done
                    ? isSplit
                      ? "text-indigo-700 font-semibold"
                      : "text-green-700"
                    : "text-slate-400"
              }`}
            >
              {p.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default OrderProgressBar;
